import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

export default function AdminAuditPage() {
  useSeo({ title: "Audit log | LUX", ...PRIVATE_SEO });
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "audit"],
    queryFn: async () => (await api.get("/admin/audit?limit=200")).data,
  });

  const verify = async () => {
    try {
      const { data } = await api.get("/admin/audit/verify");
      if (data.valid) toast.success(`Hash chain OK (${data.checked ?? 0} entries)`);
      else toast.error(`Chain broken at #${data.broken_at_seq ?? "?"} (checked ${data.checked})`);
      await refetch();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-audit-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="adm-label">Audit log</p>
          <h1 className="mt-2 text-3xl font-semibold text-admin-text">Append-only, hash-chained</h1>
          <p className="mt-1 text-sm text-admin-muted">Every staff action is written here, including this check.</p>
        </div>
        <button onClick={verify} className="adm-btn adm-btn-primary" data-testid="admin-audit-verify-button">Verify chain</button>
      </header>
      <section className="adm-card overflow-hidden" data-testid="admin-audit-log-table">
        {isLoading ? <p className="p-5 text-sm text-admin-muted">Loading...</p> : (
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-admin-surface2 text-xs uppercase text-admin-muted">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">#</th>
                  <th className="px-4 py-2 text-left font-semibold">When</th>
                  <th className="px-4 py-2 text-left font-semibold">Who</th>
                  <th className="px-4 py-2 text-left font-semibold">Action</th>
                  <th className="px-4 py-2 text-left font-semibold">Target</th>
                  <th className="px-4 py-2 text-left font-semibold">Hash</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-admin-border">
                {data?.entries?.map((e) => (
                  <tr key={e.seq} data-testid="admin-audit-row">
                    <td className="px-4 py-2 font-mono text-admin-muted">{e.seq}</td>
                    <td className="px-4 py-2 text-admin-text">{fmtDate(e.at, true)}</td>
                    <td className="px-4 py-2 text-admin-text">{e.actor_name || "System"}<br /><span className="text-xs text-admin-muted">{e.actor_role}</span></td>
                    <td className="px-4 py-2 text-admin-text">{e.action}</td>
                    <td className="px-4 py-2 text-xs text-admin-muted">{e.target_type} {e.target_id ? e.target_id.slice(0, 8) : ""}</td>
                    <td className="px-4 py-2 font-mono text-xs text-admin-muted">{(e.hash || "").slice(0, 14)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
